import { notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocMutationService, DocsService } from '@affine/core/modules/doc';
import {
  AccessibleFolderCacheStore,
  FolderStore,
} from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { useConnectivityStatus } from '../use-connectivity-status';
import { useNavigateHelper } from '../use-navigate-helper';

export function useBlockSuiteMetaHelper() {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const { openPage } = useNavigateHelper();
  const docsService = useService(DocsService);
  const docRecordList = docsService.list;
  const connectivityStatus = useConnectivityStatus();
  const isCloudWorkspace = workspace.flavour !== 'local';
  const docMutationService = useService(DocMutationService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderStore = useService(FolderStore);
  const discardLocalCloudDoc = useCallback(
    async (docId: string): Promise<boolean> => {
      await docsService.discardLocalDoc(docId);
      accessibleFolderCache.optimisticRemoveDocEverywhere(docId);
      folderStore.removeDocLinksLocalOnly(docId);
      notify.success({ title: t['Deleted']() });
      return true;
    },
    [accessibleFolderCache, docsService, folderStore, t]
  );

  // TODO-Doma
  // "Remove" may cause ambiguity here. Consider renaming as "moveToTrash".
  const removeToTrash = useCallback(
    async (docId: string): Promise<boolean> => {
      if (
        isCloudWorkspace &&
        !connectivityStatus.backendAvailable &&
        docsService.isLocallyCreated(docId)
      ) {
        return discardLocalCloudDoc(docId);
      }
      const docRecord = docRecordList.doc$(docId).value;
      if (docRecord) {
        return docRecord.moveToTrash();
      }
      return false;
    },
    [
      connectivityStatus.backendAvailable,
      discardLocalCloudDoc,
      docRecordList,
      docsService,
      isCloudWorkspace,
    ]
  );

  const restoreFromTrash = useCallback(
    (docId: string) => {
      const docRecord = docRecordList.doc$(docId).value;
      if (docRecord) {
        return docRecord.restoreFromTrash();
      }
      return false;
    },
    [docRecordList]
  );

  const permanentlyDeletePage = useCallback(
    async (
      pageId: string,
      options?: { containerCopyConfirmed?: boolean }
    ): Promise<boolean> => {
      if (isCloudWorkspace) {
        if (
          !connectivityStatus.backendAvailable &&
          docsService.isLocallyCreated(pageId)
        ) {
          return discardLocalCloudDoc(pageId);
        }
        await docMutationService.requestPermanentDelete(
          pageId,
          options?.containerCopyConfirmed ?? false
        );
        return true;
      }
      workspace.docCollection.removeDoc(pageId);
      return true;
    },
    [
      docMutationService,
      connectivityStatus.backendAvailable,
      discardLocalCloudDoc,
      docsService,
      isCloudWorkspace,
      workspace,
    ]
  );

  const duplicate = useAsyncCallback(
    async (pageId: string, openPageAfterDuplication: boolean = true) => {
      const newPageId = await docsService.duplicate(pageId);
      openPageAfterDuplication &&
        openPage(workspace.docCollection.id, newPageId);
    },
    [docsService, openPage, workspace.docCollection.id]
  );

  return {
    removeToTrash,
    restoreFromTrash,
    permanentlyDeletePage,

    duplicate,
  };
}
